import Config from '../classes/Config';
import PlayGame from './PlayGame';
import GameButton from '../classes/GameButton';
import PopInText from '../classes/PopInText';
import { Dialog } from '@capacitor/dialog';

export default class Friends extends Phaser.Scene {
  constructor() {
    super({
      key: 'Friends'
    });
  }

  panels = {};
  buttons = {};
  activeTab = 'friends';
  friendCode = '';
  friendsList = [];
  incomingRequests = [];
  outgoingRequests = [];
  searchResults = [];
  friendsTables = [];

  getWidth() {
    return this.cameras.main.width;
  }

  getHeight() {
    return this.cameras.main.height;
  }

  preload() {}

  async create() {
    this.game.dnrConfig = new Config();
    this.contentContainer = null;

    const background = new Phaser.GameObjects.Graphics(this);
    background.fillStyle(0x00a6b1, 1);
    background.fillRoundedRect(0, 0, this.getWidth(), this.getHeight(), 3);
    this.add.existing(background);

    background
      .setInteractive(
        new Phaser.Geom.Rectangle(0, 0, this.getWidth(), this.getHeight()),
        Phaser.Geom.Rectangle.Contains
      )
      .on('pointerdown', () => {});

    const isLandscape = window.showLandscape();
    const width = this.getWidth();

    let btnConfig = {};
    if (isLandscape) {
      const xChain = 50;
      const yChain = 45;
      btnConfig = {
        myFriends: { x: xChain, y: yChain, w: 235, h: 85, font: '28px Arial' },
        addFriend: { x: xChain + 250, y: yChain, w: 235, h: 85, font: '28px Arial' },
        requests: { x: xChain + 500, y: yChain, w: 235, h: 85, font: '28px Arial' },
        openTables: { x: xChain + 750, y: yChain, w: 270, h: 85, font: '24px Arial' },
        close: { x: width - 70, y: yChain, w: 50, h: 50, font: '40px Arial' }
      };
    } else {
      // Portrait mode: top title and close button, followed by 2x2 grid of tabs
      const title = this.add.text(35, 25, 'Friends', {
        font: '40px Arial Black',
        color: '#ffffff'
      });
      title.setShadow(3, 3, '#000000', 3, true);

      const margin = 30;
      const gap = 16;
      const tabW = Math.floor((width - (margin * 2) - gap) / 2);
      const tabH = 75;
      const row1Y = 90;
      const row2Y = 175;

      btnConfig = {
        myFriends: { x: margin, y: row1Y, w: tabW, h: tabH, font: '24px Arial' },
        addFriend: { x: margin + tabW + gap, y: row1Y, w: tabW, h: tabH, font: '24px Arial' },
        requests: { x: margin, y: row2Y, w: tabW, h: tabH, font: '24px Arial' },
        openTables: { x: margin + tabW + gap, y: row2Y, w: tabW, h: tabH, font: '20px Arial' },
        close: { x: width - 75, y: 20, w: 55, h: 55, font: '40px Arial' }
      };
    }

    this.buttons.myFriends = new GameButton({
      scene: this,
      x: btnConfig.myFriends.x,
      y: btnConfig.myFriends.y,
      w: btnConfig.myFriends.w,
      h: btnConfig.myFriends.h,
      depth: this.depth + 1,
      text: 'My Friends',
      font: btnConfig.myFriends.font,
      radius: 20,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('friends');
      },
      clickFunctionData: {}
    });

    this.buttons.addFriend = new GameButton({
      scene: this,
      x: btnConfig.addFriend.x,
      y: btnConfig.addFriend.y,
      w: btnConfig.addFriend.w,
      h: btnConfig.addFriend.h,
      depth: this.depth + 1,
      text: 'Add Friend',
      font: btnConfig.addFriend.font,
      radius: 20,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('add');
      },
      clickFunctionData: {}
    });

    this.buttons.requests = new GameButton({
      scene: this,
      x: btnConfig.requests.x,
      y: btnConfig.requests.y,
      w: btnConfig.requests.w,
      h: btnConfig.requests.h,
      depth: this.depth + 1,
      text: 'Requests',
      font: btnConfig.requests.font,
      radius: 20,
      borderWidth: 4,
      clickFunction: () => {
        this.switchTab('requests');
      },
      clickFunctionData: {}
    });

    this.buttons.openTables = new GameButton({
      scene: this,
      x: btnConfig.openTables.x,
      y: btnConfig.openTables.y,
      w: btnConfig.openTables.w,
      h: btnConfig.openTables.h,
      depth: this.depth + 1,
      text: 'Friend Games',
      font: btnConfig.openTables.font,
      radius: 20,
      borderWidth: 4,
      clickFunction: async () => {
        if (this.activeTab === 'tables') {
          await this.refreshTables();
        } else {
          this.switchTab('tables');
        }
      },
      clickFunctionData: {}
    });

    this.buttons.close = new GameButton({
      scene: this,
      x: btnConfig.close.x,
      y: btnConfig.close.y,
      w: btnConfig.close.w,
      h: btnConfig.close.h,
      depth: this.depth + 1,
      text: '✖',
      font: btnConfig.close.font,
      radius: 20,
      borderWidth: 4,
      clickFunction: () => {
        this.closeScene();
      },
      clickFunctionData: {}
    });

    this.contentContainer = this.add.container(0, 0);
    await this.refreshData();
    this.switchTab('friends');
  }

  closeScene() {
    this.input.topOnly = true;
    this.scene.pause('Friends');
    this.scene.setVisible(false, 'Friends');
    this.scene.stop('Friends');
  }

  showToast(message) {
    const pit = new PopInText({
      scene: this,
      x: this.getWidth() / 2,
      y: this.getHeight() / 2,
      text: message,
      box: { w: 800, h: 200, radius: 24 },
      startScale: 0.65,
      endScale: 0.75,
      scaleBy: 0.95,
      duration: 180,
      font: '48px Arial Black',
      onComplete: () => {
        pit.closeTextUpdater({ runForSeconds: 2 });
      }
    });
  }

  async refreshData() {
    try {
      const data = await this.game.friendsListFromUDS();
      if (data) {
        this.friendCode = data.friendCode || '';
        this.friendsList = data.friends || [];
        this.incomingRequests = data.incomingRequests || [];
        this.outgoingRequests = data.outgoingRequests || [];
      }
      const tablesData = await this.game.getFriendsOpenTables();
      this.friendsTables = tablesData || [];

      this.updateTabLabels();
    } catch (e) {
      console.warn('Error refreshing friends data:', e);
    }
  }

  updateTabLabels() {
    const isLandscape = window.showLandscape();
    const reqCount = this.incomingRequests ? this.incomingRequests.length : 0;
    const tblCount = this.friendsTables ? this.friendsTables.length : 0;

    if (this.buttons.requests && this.buttons.requests.text) {
      if (reqCount > 0) {
        this.buttons.requests.text.setText(`Requests (${reqCount})`);
        this.buttons.requests.text.setFontSize(isLandscape ? 24 : 20);
      } else {
        this.buttons.requests.text.setText('Requests');
        this.buttons.requests.text.setFontSize(isLandscape ? 28 : 24);
      }
    }
    if (this.buttons.openTables && this.buttons.openTables.text) {
      if (tblCount > 0) {
        this.buttons.openTables.text.setText(`Friend Games (${tblCount})`);
        this.buttons.openTables.text.setFontSize(isLandscape ? 21 : 17);
      } else {
        this.buttons.openTables.text.setText('Friend Games');
        this.buttons.openTables.text.setFontSize(isLandscape ? 24 : 20);
      }
    }
  }

  clearContent() {
    if (this.activeScrollPanel) {
      this.activeScrollPanel.destroy();
      this.activeScrollPanel = null;
    }
    if (this.contentContainer) {
      this.contentContainer.removeAll(true);
    }
  }

  async refreshTables() {
    if (this._isRefreshingTables) return;
    this._isRefreshingTables = true;
    try {
      const tablesData = await this.game.getFriendsOpenTables();
      this.friendsTables = tablesData || [];
      this.updateTabLabels();
      if (this.activeTab === 'tables') {
        this.renderActiveTab();
      }
    } catch (e) {
      console.warn('Error refreshing tables:', e);
    } finally {
      this._isRefreshingTables = false;
    }
  }

  switchTab(tab) {
    this.activeTab = tab;
    this.buttons.myFriends.unselect();
    this.buttons.addFriend.unselect();
    this.buttons.requests.unselect();
    this.buttons.openTables.unselect();

    if (tab === 'friends') this.buttons.myFriends.select();
    else if (tab === 'add') this.buttons.addFriend.select();
    else if (tab === 'requests') this.buttons.requests.select();
    else if (tab === 'tables') this.buttons.openTables.select();

    this.renderActiveTab();
    if (tab === 'tables') {
      this.refreshTables();
    }
  }

  renderActiveTab() {
    this.clearContent();
    const isLandscape = window.showLandscape();
    const startX = isLandscape ? 50 : 30;
    const width = this.getWidth() - (isLandscape ? 100 : 60);
    const bannerY = isLandscape ? 145 : 265;
    const bannerH = 75;

    // Friend Code banner at top of all tabs
    const codeBannerBg = this.add.graphics();
    codeBannerBg.fillStyle(0xffffff, 0.95);
    codeBannerBg.fillRoundedRect(startX, bannerY, width, bannerH, 14);
    this.contentContainer.add(codeBannerBg);

    const codeLabel = this.add.text(
      startX + 20,
      bannerY + bannerH / 2,
      isLandscape ? `Your Friend Code:  ${this.friendCode || '...'}` : `Code: ${this.friendCode || '...'}`,
      { font: isLandscape ? '32px Arial Black' : '26px Arial Black', color: '#111111' }
    ).setOrigin(0, 0.5);
    this.contentContainer.add(codeLabel);

    const copyBtnW = isLandscape ? 240 : 140;
    const copyBtn = new GameButton({
      scene: this,
      x: startX + width - copyBtnW - 12,
      y: bannerY + 10,
      w: copyBtnW,
      h: 55,
      text: isLandscape ? 'Copy Code' : 'Copy',
      font: isLandscape ? '26px Arial' : '24px Arial',
      radius: 14,
      borderWidth: 2,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        try {
          if (navigator.clipboard && this.friendCode) {
            await navigator.clipboard.writeText(this.friendCode);
            this.showToast(`Copied Friend Code: ${this.friendCode}`);
          } else {
            await Dialog.alert({ title: 'Your Friend Code', message: this.friendCode });
          }
        } catch {
          await Dialog.alert({ title: 'Your Friend Code', message: this.friendCode });
        }
      },
      clickFunctionData: {}
    });

    const contentY = bannerY + bannerH + 15;
    if (this.activeTab === 'friends') {
      this.renderMyFriendsTab(startX, width, contentY);
    } else if (this.activeTab === 'add') {
      this.renderAddFriendTab(startX, width, contentY);
    } else if (this.activeTab === 'requests') {
      this.renderRequestsTab(startX, width, contentY);
    } else if (this.activeTab === 'tables') {
      this.renderFriendsTablesTab(startX, width, contentY);
    }
  }

  renderMyFriendsTab(startX, width, contentY) {
    const isLandscape = window.showLandscape();
    const height = this.getHeight() - contentY - (isLandscape ? 40 : 30);

    if (this.friendsList.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(startX, contentY, width, 360, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        startX + width / 2,
        contentY + 130,
        'You have not added any friends yet.\n\nUse "Add Friend" to search\nby Display Name or Friend Code!',
        {
          font: isLandscape ? '30px Arial' : '24px Arial',
          color: '#333333',
          align: 'center',
          wordWrap: { width: width - 40, useAdvancedWrap: true }
        }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);

      const addNowBtn = new GameButton({
        scene: this,
        x: startX + width / 2 - 150,
        y: contentY + 245,
        w: 300,
        h: 75,
        text: 'Add a Friend',
        font: '32px Arial',
        radius: 18,
        borderWidth: 3,
        addToScene: false,
        parentContainer: this.contentContainer,
        clickFunction: () => this.switchTab('add'),
        clickFunctionData: {}
      });
      return;
    }

    const items = this.friendsList.map((f) => ({
      dnrId: f.dnrId,
      avatar: this.game.getAvatarObjFromAvatarKey(f.avatar).avatar,
      label: `${f.name}\nLvl ${f.level} (${f.friendCode})`,
      rawName: f.name
    }));

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: startX,
      y: contentY,
      width,
      height,
      titleText: `Friends (${items.length}) — Tap a friend to manage`,
      onChildClick: async (child) => {
        if (this._isActionBusy) return;
        this._isActionBusy = true;
        try {
          const friend = this.friendsList.find((f) => f.dnrId === child.name);
          if (!friend) return;
          const openTbl = this.friendsTables.find(
            (t) => t.host_id === friend.dnrId || (t.friend_names || []).includes(friend.name)
          );
          if (openTbl) {
            const { value } = await Dialog.confirm({
              title: friend.name,
              message: `${friend.name} is at an open table (${openTbl.id}). Join their game?`
            });
            if (value) {
              this.joinTableAndStart(openTbl.id);
              return;
            }
          }
          const { value: confirmRemove } = await Dialog.confirm({
            title: `Remove ${friend.name}?`,
            message: `Do you want to remove ${friend.name} (${friend.friendCode}) from your friends list?`
          });
          if (confirmRemove) {
            await this.game.removeFriendOnUDS(friend.dnrId);
            await this.refreshData();
            this.renderActiveTab();
            this.showToast(`Removed ${friend.name}`);
          }
        } finally {
          setTimeout(() => { this._isActionBusy = false; }, 400);
        }
      }
    });
  }

  renderAddFriendTab(startX, width, contentY) {
    const isLandscape = window.showLandscape();
    const btnGap = 16;
    const btnW = isLandscape ? 340 : Math.floor((width - btnGap) / 2);
    const btnH = 75;

    const enterCodeBtn = new GameButton({
      scene: this,
      x: startX,
      y: contentY,
      w: btnW,
      h: btnH,
      text: isLandscape ? 'Add by Friend Code' : 'Friend Code',
      font: isLandscape ? '30px Arial' : '26px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        if (this._isActionBusy) return;
        this._isActionBusy = true;
        try {
          const { value, cancelled } = await Dialog.prompt({
            title: 'Add by Friend Code',
            message: "Enter your friend's 6-character Friend Code:",
            inputText: ''
          });
          if (!cancelled && value && value.trim()) {
            const res = await this.game.sendFriendRequestToUDS({ friendCode: value.trim() });
            await this.refreshData();
            this.showToast(res?.message || res?.error || 'Request processed');
            if (this.activeTab === 'add') this.renderActiveTab();
          }
        } finally {
          setTimeout(() => { this._isActionBusy = false; }, 400);
        }
      },
      clickFunctionData: {}
    });

    const searchNameBtn = new GameButton({
      scene: this,
      x: startX + btnW + btnGap,
      y: contentY,
      w: btnW,
      h: btnH,
      text: isLandscape ? 'Search by Name' : 'Search Name',
      font: isLandscape ? '30px Arial' : '26px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        if (this._isActionBusy) return;
        this._isActionBusy = true;
        try {
          const { value, cancelled } = await Dialog.prompt({
            title: 'Search Players',
            message: 'Enter player Display Name or Friend Code:',
            inputText: ''
          });
          if (!cancelled && value && value.trim()) {
            const results = await this.game.searchFriendsOnUDS(value.trim());
            this.searchResults = results || [];
            this.renderActiveTab();
          }
        } finally {
          setTimeout(() => { this._isActionBusy = false; }, 400);
        }
      },
      clickFunctionData: {}
    });

    const subContentY = contentY + btnH + 15;
    const subHeight = this.getHeight() - subContentY - (isLandscape ? 40 : 30);

    if (this.searchResults.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(startX, subContentY, width, 300, 14);
      this.contentContainer.add(panelBg);

      const hint = this.add.text(
        startX + width / 2,
        subContentY + 150,
        'Tap "Add by Friend Code" to enter a 6-character code, or tap "Search by Name" to find players by Display Name.',
        {
          font: isLandscape ? '28px Arial' : '22px Arial',
          color: '#333333',
          align: 'center',
          wordWrap: { width: width - 40, useAdvancedWrap: true }
        }
      ).setOrigin(0.5);
      this.contentContainer.add(hint);
      return;
    }

    const items = this.searchResults.map((r) => {
      let statusTag = 'Tap to Add';
      if (r.friendshipStatus === 'accepted') statusTag = 'Friends ✓';
      else if (r.friendshipStatus === 'pending') statusTag = 'Pending...';
      return {
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\n${statusTag}`,
        status: r.friendshipStatus
      };
    });

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: startX,
      y: subContentY,
      width,
      height: subHeight,
      titleText: `Search Results (${items.length}) — Tap to send request`,
      onChildClick: async (child) => {
        if (this._isActionBusy) return;
        this._isActionBusy = true;
        try {
          const target = this.searchResults.find((r) => r.dnrId === child.name);
          if (!target) return;
          if (target.friendshipStatus === 'accepted') {
            this.showToast(`You are already friends with ${target.name}`);
            return;
          }
          const res = await this.game.sendFriendRequestToUDS({ targetDnrId: target.dnrId });
          target.friendshipStatus = res?.status || 'pending';
          await this.refreshData();
          this.renderActiveTab();
          this.showToast(res?.message || `Sent request to ${target.name}`);
        } finally {
          setTimeout(() => { this._isActionBusy = false; }, 400);
        }
      }
    });
  }

  renderRequestsTab(startX, width, contentY) {
    const isLandscape = window.showLandscape();
    const height = this.getHeight() - contentY - (isLandscape ? 40 : 30);
    const combined = [
      ...this.incomingRequests.map((r) => ({
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\nIncoming — Tap to Accept`,
        direction: 'incoming',
        rawName: r.name
      })),
      ...this.outgoingRequests.map((r) => ({
        dnrId: r.dnrId,
        avatar: this.game.getAvatarObjFromAvatarKey(r.avatar).avatar,
        label: `${r.name} (${r.friendCode})\nSent (Pending)`,
        direction: 'outgoing',
        rawName: r.name
      }))
    ];

    if (combined.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(startX, contentY, width, 340, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        startX + width / 2,
        contentY + 170,
        'No pending friend requests right now.',
        {
          font: isLandscape ? '30px Arial' : '24px Arial',
          color: '#333333',
          align: 'center',
          wordWrap: { width: width - 40, useAdvancedWrap: true }
        }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);
      return;
    }

    this.activeScrollPanel = this.createGridPanel({
      items: combined,
      x: startX,
      y: contentY,
      width,
      height,
      titleText: `Friend Requests (${combined.length})`,
      onChildClick: async (child) => {
        if (this._isActionBusy) return;
        this._isActionBusy = true;
        try {
          const item = combined.find((c) => c.dnrId === child.name);
          if (!item) return;
          if (item.direction === 'incoming') {
            const { value } = await Dialog.confirm({
              title: 'Friend Request',
              message: `Accept friend request from ${item.rawName}? (Cancel to decline)`
            });
            await this.game.respondFriendRequestOnUDS(item.dnrId, value ? 'accept' : 'decline');
            await this.refreshData();
            this.renderActiveTab();
            this.showToast(value ? `Added ${item.rawName}!` : 'Request declined');
          } else {
            const { value } = await Dialog.confirm({
              title: 'Cancel Request?',
              message: `Cancel your pending friend request to ${item.rawName}?`
            });
            if (value) {
              await this.game.removeFriendOnUDS(item.dnrId);
              await this.refreshData();
              this.renderActiveTab();
              this.showToast('Request cancelled');
            }
          }
        } finally {
          setTimeout(() => { this._isActionBusy = false; }, 400);
        }
      }
    });
  }

  renderFriendsTablesTab(startX, width, contentY) {
    const isLandscape = window.showLandscape();
    const refreshBtn = new GameButton({
      scene: this,
      x: startX,
      y: contentY,
      w: 260,
      h: 65,
      text: 'Refresh Tables',
      font: '28px Arial',
      radius: 16,
      borderWidth: 3,
      addToScene: false,
      parentContainer: this.contentContainer,
      clickFunction: async () => {
        await this.refreshTables();
      },
      clickFunctionData: {}
    });

    const subContentY = contentY + 80;
    const subHeight = this.getHeight() - subContentY - (isLandscape ? 40 : 30);

    if (this.friendsTables.length === 0) {
      const panelBg = this.add.graphics();
      panelBg.fillStyle(0xffffff, 0.95);
      panelBg.fillRoundedRect(startX, subContentY, width, 300, 14);
      this.contentContainer.add(panelBg);

      const emptyText = this.add.text(
        startX + width / 2,
        subContentY + 150,
        'No friends are currently waiting at an open table.\nStart a Custom Game in Avalanche and invite them!',
        {
          font: isLandscape ? '28px Arial' : '22px Arial',
          color: '#333333',
          align: 'center',
          wordWrap: { width: width - 40, useAdvancedWrap: true }
        }
      ).setOrigin(0.5);
      this.contentContainer.add(emptyText);
      return;
    }

    const items = this.friendsTables.map((t) => {
      const privacyLabel = t.privacy === 'friends' ? 'Friends Only' : 'Public';
      const hostFriend = (this.friendsList || []).find((f) => f.dnrId === t.host_id || f.name === t.host_name);
      const rawAvatar = hostFriend?.avatar || t.host_avatar;
      const avatarObj = this.game.getAvatarObjFromAvatarKey(rawAvatar);
      const avatarKey = avatarObj ? avatarObj.avatar : 'mr_aces';
      return {
        dnrId: t.id,
        avatar: avatarKey,
        label: `Host: ${t.host_name} (${t.id})\n${t.players}/${t.max_players} Players • ${privacyLabel}`
      };
    });

    this.activeScrollPanel = this.createGridPanel({
      items,
      x: startX,
      y: subContentY,
      width,
      height: subHeight,
      titleText: `Friends' Open Tables (${items.length}) — Tap to Join`,
      onChildClick: (child) => {
        if (child.name) {
          this.joinTableAndStart(child.name);
        }
      }
    });
  }

  joinTableAndStart(tableId) {
    this.game.pendingJoinTableId = tableId;
    this.closeScene();
    if (this.scene.isActive('GameSelection')) {
      this.scene.start('PlayGame', 'Avalanche').stop('GameSelection');
    } else if (this.scene.isActive('PlayGame')) {
      const pg = this.scene.get('PlayGame');
      if (pg) {
        pg.joinCustomTableById(tableId);
      }
    }
  }

  createGridPanel({ items, x, y, width, height, titleText, onChildClick }) {
    const isLandscape = window.showLandscape();
    const columns = isLandscape ? Math.max(1, Math.min(3, items.length)) : 1;
    const rows = Math.max(1, Math.ceil(items.length / columns));

    const scrollablePanel = this.rexUI.add
      .scrollablePanel({
        x,
        y,
        width,
        height,
        scrollMode: 0,
        background: this.rexUI.add.roundRectangle(0, 0, 2, 2, 12, 0xffffff),
        panel: {
          child: this.rexUI.add
            .fixWidthSizer({
              space: { left: 12, right: 12, top: 15, bottom: 10, item: 12, line: 12 }
            })
            .enableLayoutWarn(false),
          width: width - 60,
          mask: { mask: true, padding: { right: -15 }, updateMode: 'everyTick' }
        },
        scroller: {},
        slider: {
          track: this.rexUI.add.roundRectangle(0, 0, 16, 10, 8, PlayGame.COLOR_LIGHT),
          thumb: this.rexUI.add.roundRectangle(0, 0, 0, 0, 16, PlayGame.COLOR_GOLD)
        },
        space: { left: 12, right: 12, top: 10, bottom: 10, panel: 10 }
      })
      .enableLayoutWarn(false)
      .setOrigin(0)
      .layout();

    const table = this.rexUI.add
      .gridSizer({
        column: columns,
        row: rows,
        rowProportions: 1,
        space: { column: 18, row: 18 }
      })
      .enableLayoutWarn(false);

    table.setChildrenInteractive().on('child.click', onChildClick, this);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const c = i % columns;
      const r = Math.floor(i / columns);
      const label = this.rexUI.add
        .label({
          background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 14, PlayGame.COLOR_GOLD),
          orientation: 'x',
          icon: item.avatar ? this.add.sprite(0, 0, item.avatar).setScale(0.32) : null,
          text: this.add.text(0, 0, item.label, { font: isLandscape ? '24px Arial Black' : '22px Arial Black', color: '#000000' }),
          name: item.dnrId,
          space: { left: 16, right: 16, top: 12, bottom: 12, icon: 12 }
        })
        .enableLayoutWarn(false);
      table.add(label, c, r, 'top', 0, true);
    }

    const title = this.rexUI.add.label({
      width: width - 80,
      orientation: 'x',
      text: this.add.text(0, 0, titleText, { font: isLandscape ? '28px Arial Black' : '22px Arial Black', color: '#000000' })
    });

    scrollablePanel.getElement('panel').add(title, 0, 'left', 0, true);
    scrollablePanel.getElement('panel').add(table, 1, 'left', 0, true);
    scrollablePanel.layout();
    return scrollablePanel;
  }
}
